/**
 * src/lib/server/strip-vectors.ts
 *
 * Server-only prop projection: strip the heavy `embedding` vector (a 2048-float
 * Firestore `vectorValue`) and its `embeddingMeta` companion off Firestore docs
 * (tags, articles, …) BEFORE they cross the RSC boundary into client components.
 *
 * WHY: `tagService` / `articlesService` map docs with a `{ ...data }` spread, so
 * the embedding field rides straight through into every Tag/Article passed as a
 * prop. Each vector serializes to ~26 KB in the RSC flight payload — on entity
 * pages (which serialize dozens of tags: the entity + associated + related +
 * asset-resolved) this was ~60-65% of the entire HTML response (e.g. pengu:
 * 1.60 MB of 2.47 MB). NOTHING on the page reads these fields — they are absent
 * from the `Tag`/`Article` TS types, and no component accesses `.embedding` /
 * `.embeddingMeta`. They are pure dead weight on the wire.
 *
 * This ONLY removes those two keys; every other field (including `content`,
 * `glossaryContent`, `glossaryContentMeta`, `images`, `description`, …) is
 * preserved verbatim, so rendered output is byte-identical. Returns a shallow
 * clone (never mutates the input).
 */

const HEAVY_KEYS = ['embedding', 'embeddingMeta'] as const;

/** Strip embedding fields from one doc-shaped object. Returns a shallow clone. */
export function stripVectors<T>(obj: T): T {
  if (!obj || typeof obj !== 'object') return obj;
  const out = { ...(obj as Record<string, unknown>) };
  for (const key of HEAVY_KEYS) {
    if (key in out) delete out[key];
  }
  return out as T;
}

/** Strip embedding fields from every element of an array (null-safe passthrough). */
export function stripVectorsAll<T>(arr: T[] | undefined | null): T[] {
  if (!arr) return [];
  return arr.map(stripVectors);
}
